// src/pages/SubmissionSuccess.jsx
import { Link, useLocation } from "react-router-dom";
import apiService from "../services/apiService";
import { COMPANY } from "../constants/company";

/** Utilities */
const inr = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n);





  

const SectionCard = ({ title, children, className = "" }) => (
  <section
    className={`rounded-2xl border border-gray-200 bg-white shadow-sm p-5 sm:p-6 ${className}`}
  >
    {title && <h3 className="mb-3 text-sm font-semibold text-gray-900">{title}</h3>}
    {children}
  </section>
);

const Row = ({ label, value }) => (
  <div className="grid grid-cols-12 gap-2 text-sm">
    <dt className="col-span-4 text-gray-500">{label}</dt>
    <dd className="col-span-8 font-medium text-gray-900 break-words">
      {value || "-"}
    </dd>
  </div>
);

const Pill = ({ children }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200 px-2.5 py-1 text-xs font-medium">
    <svg
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    {children}
  </span>
);

export default function SubmissionSuccess() {
  const { state } = useLocation();

  /** ✅ Data from navigate("/success", { state: { ... } }) */
  const data = state?.data || state || {};

  // ✅ Extract from response
  const {
    fullName,
    email,
    mobile,
    pan,
    dob,
    amount,
    paymentDate,
    txnId,
    agentName,
    panDoc,
    aadharDoc,
    createdAt,
  } = data || {};

  // ✅ Example fallback transaction
  const transactions = [
    {
      id: 1,
      amount: amount,
      date: paymentDate,
      txnId: txnId,
      agent: agentName,
    },
  ];

  // ✅ Invoice link (if you generated one)

  
  const invoiceUrl = `${COMPANY.API_BASE_URL}/api/invoice/${txnId}`;

  return (
    <div className="min-h-screen w-full bg-gray-50 py-10 px-4">
      <div className="mx-auto max-w-3xl">
        <div className="rounded-3xl border border-gray-200 bg-white shadow-[0_20px_60px_-20px_rgba(16,185,129,0.25)] overflow-hidden">
          {/* Header */}
          <div className="relative isolate">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-50 via-white to-cyan-50" />
            <div className="relative p-6 sm:p-8 border-b border-gray-200">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-semibold text-emerald-700">
                      Submission Successful
                    </h1>
                    <Pill>Success</Pill>
                  </div>
                  <p className="mt-1 text-sm text-gray-600">
                    We’ve processed your details and sent a confirmation email
                    with your invoice.
                  </p>
                </div>
                {/* Check icon */}
                <div className="hidden sm:flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
                  <svg
                    viewBox="0 0 24 24"
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      d="M20 6L9 17l-5-5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="space-y-5 p-6 sm:p-8 bg-gray-50">
            {/* Customer Info */}
            <SectionCard title="Customer Information">
              <dl className="grid grid-cols-1 gap-y-3 sm:grid-cols-2 sm:gap-6">
                <Row label="Full Name:" value={fullName} />
                <Row label="Email:" value={email} />
                <Row label="Mobile:" value={mobile} />
                <Row label="PAN:" value={pan} />
                <Row label="Date of Birth:" value={dob} />
              </dl>
            </SectionCard>

            {/* Subscription */}
            <SectionCard title="Subscription Details">
              <dl className="grid grid-cols-1 gap-y-3 sm:grid-cols-2 sm:gap-6">
                <Row label="Amount Paid:" value={inr(amount)} />
                <Row label="Payment Date:" value={paymentDate} />
                <Row label="Transaction ID:" value={txnId} />
                <Row label="Agent Name:" value={agentName} />
              </dl>
            </SectionCard>

            {/* Uploaded Documents */}
            <SectionCard title="Uploaded Documents">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="text-sm">
                  <p className="font-medium text-gray-800 mb-1">PAN Document:</p>
                  {panDoc?.url ? (
                    <a
                      href={panDoc.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-700 underline hover:text-blue-900"
                    >
                      View PAN
                    </a>
                  ) : (
                    <span className="text-gray-500">Not Uploaded</span>
                  )}
                </div>
                <div className="text-sm">
                  <p className="font-medium text-gray-800 mb-1">Aadhar Document:</p>
                  {aadharDoc?.url ? (
                    <a
                      href={aadharDoc.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-700 underline hover:text-blue-900"
                    >
                      View Aadhar
                    </a>
                  ) : (
                    <span className="text-gray-500">Not Uploaded</span>
                  )}
                </div>
              </div>
            </SectionCard>

            {/* Invoice */}
            <SectionCard title="Invoice">
              <div className="text-sm">
                <span className="text-gray-600">Invoice PDF: </span>
                <a
                  href={invoiceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-blue-700 underline hover:text-blue-800"
                >
                  Open Invoice
                </a>
              </div>
            </SectionCard>

            {/* Transaction History */}
            <SectionCard title="Transaction History" className="bg-gray-50">
              <ul className="space-y-3">
                {transactions.map((t) => (
                  <li
                    key={t.id}
                    className="flex items-start justify-between gap-4 rounded-2xl border border-gray-200 bg-white p-4"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-700">
                        #{t.id}
                      </div>
                      <div>
                        <div className="text-base font-semibold text-gray-900">
                          {inr(t.amount)}
                        </div>
                        <div className="mt-1 text-sm text-gray-600">
                          <div>Transaction ID: {t.txnId}</div>
                          <div>Agent: {t.agent}</div>
                        </div>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500">{t.date}</div>
                  </li>
                ))}
              </ul>
            </SectionCard>

            {/* Footer Links */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Link
                to="/"
                className="text-sm font-medium text-blue-700 underline underline-offset-2 hover:text-blue-800"
              >
                Submit Another Response
              </Link>
              <div className="flex items-center gap-4 text-sm">
                <Link
                  to="/agreement"
                  state={{ fullName, email, mobile }}
                  className="font-medium text-blue-700 underline underline-offset-2 hover:text-blue-800"
                >
                  Review User Agreement
                </Link>
                <a
                  href="#"
                  className="font-medium text-gray-600 hover:text-gray-800"
                >
                  Terms and Conditions
                </a>
              </div>
            </div>

            {/* Metadata */}
            <p className="text-xs text-gray-400 text-center pt-4">
              Created at: {new Date(createdAt).toLocaleString("en-IN")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
